export interface ITotalDataService {
  getTransactionsTable: (
    page: number,
    pageSize: number,
    id: number,
    filter: TransactionFilterType,
    sortRow?: string,
    sortOrder?: string
  ) => Promise<FilteredTransacation | []>
  getUserCount: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalDeposit: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalWithdrawal: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalPendingWithdrawal: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalWager: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalWins: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalRakeback: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalAffiliate: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
  getTotalPromoCode: (dates: (Date | null)[]) => Promise<StateResponse | undefined>
}

export type StatsResponse = {
  wager: number
  win: number
  ggr: number
  ngr: number
  ngrInputs?: NgrInputs
  freespinWinnings?: number

  rakeback: number
  reward: number
  vipReward: number
  affiliate: number
  promo: number
  bonus: number

  deposit: number
  withdrawal: number
  unconfirmedWithdrawal: number
  netDeposits: number
  withdrawalCount: number

  activeUsers: number
  depositers: number
  depositCount: number
  ftdSum: number
  averageBet: number
  pp?: StatsResponse
}

export type NgrInputs = {
  ggr: number
  bonus: number
  cancelledBonusAmount: number
  deposit: number
  withdrawal: number
  positiveGgrFees: number
}

export type BettingStatsResponse = {
  wager: number
  win: number
  ggr: number
  averageBet: number
  positiveGgrFees: number
  balanceCorrections: number
  ttp?: Record<string, number>
}

export type TransactionStatsResponse = {
  deposit: number
  withdrawal: number
  unconfirmedWithdrawal: number
  depositCount: number
  withdrawalCount: number
  activeUsers: number
  rakeback: number
  reward: number
  vipReward: number
  affiliate: number
  promo: number
  bonus: number
  cancelledBonusAmount: number
  ttp?: Record<string, number>
}

export type DepositorStatsResponse = {
  ftdSum: number
  depositers: number
  ttp?: Record<string, number>
}

export interface PeriodStats {
  year: string
  wager: number
  win: number
  deposit: number
  withdrawal: number
  netDeposits: number
  depositCount: number
  withdrawalCount: number
  activeUsers: number
  ftdSum: number
  depositers: number
  averageBet: number
  userCount: number
}

export interface TimeSeriesStatsResponse {
  year: string
  wager: number
  win: number
  deposit: number
  withdrawal: number
  netDeposits: number
  depositCount: number
  withdrawalCount: number
  activeUsers: number
  ftdSum: number
  depositers: number
  averageBet: number
  userCount: number
}

export interface YearlyStatsResponse {
  year: string
  wager: number
  win: number
  deposit: number
  withdrawal: number
  netDeposits: number
  depositCount: number
  withdrawalCount: number
  activeUsers: number
  ftdSum: number
  depositers: number
  averageBet: number
  userCount: number
}

export type StateResponse = {
  data: number
}

export type TransactionFilter = {
  page: number
  pageSize: number
  id: number
  sortRow?: string
  sortOrder?: string
}

// ─── GGR Summary Types ────────────────────────────────────────────────────────

export interface GgrByProviderRow {
  provider: string
  providerTitle: string
  wager: number
  win: number
  ggr: number
  betCount: number
}

export interface GgrByCountryRow {
  country: string
  wager: number
  win: number
  ggr: number
  betCount: number
  playerCount: number
}

export interface GgrByPlayerRow {
  userId: string
  username: string | null
  displayName: string | null
  email: string | null
  wager: number
  win: number
  ggr: number
  betCount: number
}

export interface GgrSummaryResponse {
  byProvider: GgrByProviderRow[]
  byCountry: GgrByCountryRow[]
  byPlayer: GgrByPlayerRow[]
}

export enum GgrSummaryType {
  Provider = "provider",
  Country = "country",
  Player = "player"
}

export type TransactionFilterType = { type: string; status: string; createdAt?: (Date | null)[] }

export type FilteredTransacation = {
  page: number
  pageSize: number
  total: number
  total_pages: number
  data: Transaction[]
}

export type Transaction = {
  id: string
  user_id: string
  user: string
  amount: string
  usd_amount: null | string
  status: string
  coin: null | string
  withdrawal_address: null | string
  total_balance?: number | null
  total_balance_coin?: string
  balance_unavailable?: boolean
  bonus_violation?: boolean | null
  type: string
  payout_id: null | string
  other: Record<string, unknown> | null
  provider: null | string
  created_at: Date
}
